// useJamEngine — binds an <audio> element (the timing source of truth) to the
// JamEngine, runs the per-frame update loop, owns the Recovery state machine,
// and exposes developer overrides. All screens read the returned JamState.

import { useCallback, useEffect, useRef, useState } from "react";
import { GuidanceMode, JamState, Song } from "@/models/types";
import { computeJamState } from "@/engine/JamEngine";

interface UseJamEngineOptions {
  song: Song;
  mode: GuidanceMode;
  startTime?: number;
  onEnded?: () => void;
}

export interface JamEngineControls {
  audioRef: React.RefObject<HTMLAudioElement>;
  jamState: JamState;
  isPlaying: boolean;
  recoveries: number;
  transitionsHit: number;
  transitionsTotal: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  restart: () => void;
  seek: (t: number) => void;
  triggerRecovery: () => void;
  forceRecoveryDepth: (d: number | null) => void;
  jumpToNextSection: () => void;
  jumpToNextTransition: () => void;
}

export function useJamEngine(opts: UseJamEngineOptions): JamEngineControls {
  const { song, mode, startTime = 0, onEnded } = opts;
  const beat = 60 / song.bpm;

  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number | null>(null);
  const prevTimeRef = useRef<number>(startTime);

  const modeRef = useRef<GuidanceMode>(mode);
  modeRef.current = mode;

  const recoveryStartRef = useRef<number | null>(null);
  const forcedDepthRef = useRef<number | null>(null);
  const recoveriesRef = useRef(0);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const [jamState, setJamState] = useState<JamState>(() =>
    computeJamState({ song, time: startTime, mode, recoveryLevel: 0 })
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [recoveries, setRecoveries] = useState(0);
  const [transitionsHit, setTransitionsHit] = useState(0);
  const transitionsTotal = song.timeline.filter((e) => e.transition).length;

  const resolveRecoveryLevel = useCallback(
    (t: number): number => {
      if (forcedDepthRef.current !== null) return forcedDepthRef.current;
      if (recoveryStartRef.current === null) return 0;
      const elapsedBeats = (t - recoveryStartRef.current) / beat;
      if (elapsedBeats < 3) return 3;
      if (elapsedBeats < 5.5) return 2;
      if (elapsedBeats < 8) return 1;
      recoveryStartRef.current = null;
      return 0;
    },
    [beat]
  );

  const computeNow = useCallback(() => {
    const a = audioRef.current;
    const t = a ? a.currentTime : prevTimeRef.current;

    // count transitions we crossed since the last frame
    for (const e of song.timeline) {
      if (e.transition && prevTimeRef.current < e.time && t >= e.time) {
        setTransitionsHit((n) => n + 1);
      }
    }
    prevTimeRef.current = t;

    const rl = resolveRecoveryLevel(t);
    setJamState(computeJamState({ song, time: t, mode: modeRef.current, recoveryLevel: rl }));
  }, [song, resolveRecoveryLevel]);

  const loop = useCallback(() => {
    computeNow();
    rafRef.current = requestAnimationFrame(loop);
  }, [computeNow]);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.play();
    setIsPlaying(true);
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    computeNow();
  }, [computeNow]);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const seek = useCallback(
    (t: number) => {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.max(0, Math.min(t, song.duration - 0.05));
      prevTimeRef.current = a.currentTime;
      computeNow();
    },
    [song.duration, computeNow]
  );

  const restart = useCallback(() => {
    recoveryStartRef.current = null;
    forcedDepthRef.current = null;
    recoveriesRef.current = 0;
    setRecoveries(0);
    setTransitionsHit(0);
    seek(0);
  }, [seek]);

  const triggerRecovery = useCallback(() => {
    const a = audioRef.current;
    recoveryStartRef.current = a ? a.currentTime : prevTimeRef.current;
    forcedDepthRef.current = null;
    recoveriesRef.current += 1;
    setRecoveries(recoveriesRef.current);
    computeNow();
  }, [computeNow]);

  const forceRecoveryDepth = useCallback(
    (d: number | null) => {
      forcedDepthRef.current = d;
      if (d === null) recoveryStartRef.current = null;
      computeNow();
    },
    [computeNow]
  );

  const jumpToNextSection = useCallback(() => {
    const t = prevTimeRef.current;
    const next = song.timeline.find((e) => e.section && e.time > t + 0.1);
    if (next) seek(next.time + 0.05);
  }, [song.timeline, seek]);

  const jumpToNextTransition = useCallback(() => {
    const t = prevTimeRef.current;
    const bar = beat * song.timeSignature[0];
    const next = song.timeline.find((e) => e.transition && e.time > t + 0.1);
    if (next) seek(Math.max(0, next.time - 3 * bar + 1.5));
  }, [song.timeline, seek, beat, song.timeSignature]);

  // attach audio listeners & apply startTime once metadata is ready
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const applyStart = () => {
      if (startTime > 0) {
        a.currentTime = startTime;
        prevTimeRef.current = startTime;
        computeNow();
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      onEndedRef.current?.();
    };
    a.addEventListener("loadedmetadata", applyStart);
    a.addEventListener("ended", handleEnded);
    if (a.readyState >= 1) applyStart();
    return () => {
      a.removeEventListener("loadedmetadata", applyStart);
      a.removeEventListener("ended", handleEnded);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    audioRef,
    jamState,
    isPlaying,
    recoveries,
    transitionsHit,
    transitionsTotal,
    play,
    pause,
    toggle,
    restart,
    seek,
    triggerRecovery,
    forceRecoveryDepth,
    jumpToNextSection,
    jumpToNextTransition,
  };
}
